import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import moment from 'moment';
import T from 'Components/Helpers/translate';
import CompanyDossier, { SyncToCrmResult } from '../ProspectingAgent/CompanyDossier';
import LegacyEmbed from './LegacyEmbed';
import type { ResultCompany } from './useResults';

/**
 * Company detail as a slide-over (WEB-10886, design 2a · b): a 760px panel
 * pinned to the right over a light scrim, so the panes stay live behind it. It
 * slides in from the edge, shows the company's header at once and a shimmer
 * where the dossier is still loading. Esc or the × returns to where the rep
 * was, scroll position and selection intact.
 *
 * The panel hosts the V1 `CompanyDossier` unchanged - summary, signals,
 * decision-makers, plan, sources, and the actions. Two callers feed it: a
 * search row (mapped by `searchCompanyToCandidate`) and a History run's
 * candidate, which arrives with its run-scoped import/sync handlers.
 */

/** The candidate shape `CompanyDossier` consumes. */
export type DrawerCandidate = Record<string, any> & { prospectingId: string; name?: string };

const town = (row: ResultCompany) => row.visitingTown || row.postTown || row.registeredTown || undefined;

/**
 * A search row knows only what the index does; the dossier settles the rest
 * from the enrichment cache. Whether the CRM already holds the company is the
 * exception: the search matched it (WEB-11033), or this session imported it,
 * and either way the dossier opens on the Sync action rather than asking.
 */
export const searchCompanyToCandidate = (
	company: ResultCompany,
	runId: number | null,
	clientId: number | undefined
): DrawerCandidate => ({
	prospectingId: company.prospectingId,
	name: company.name,
	domain: company.website?.replace(/^https?:\/\//, ''),
	countryCode: company.countryCode ?? undefined,
	// Employees and revenue belong to the company, so only a headquarters row
	// may show them - the same rule the table applies (WEB-9700).
	employees: company.headquarters ? company.noEmployees ?? undefined : undefined,
	revenue: company.headquarters ? company.revenue ?? undefined : undefined,
	city: town(company),
	naceCode: company.naceCode ?? undefined,
	founded: company.registrationDate ? moment(company.registrationDate).format('YYYY') : undefined,
	orgNumber: company.orgNumber ?? undefined,
	runId: runId ?? undefined,
	...(company.inCRM && company.crmClient
		? { inCRM: true, crmClient: company.crmClient }
		: clientId
		? {
				inCRM: true,
				crmClient: { id: clientId, name: company.name, matchReason: company.orgNumber ? 'orgNo' : 'domain' }
		  }
		: {})
});

type Props = {
	candidate: DrawerCandidate;
	/** Where the scrim starts, so live panes to the left are not covered. */
	left: number;
	onClose: () => void;
	onImport: (prospectingIds: string[], contactNames?: string[]) => void;
	onSync: (
		prospectingId: string,
		clientId: number,
		mode: 'sync' | 'new',
		contactNames?: string[]
	) => Promise<SyncToCrmResult | void> | void;
	onEnrichStarted?: (runId: number) => void;
	onPlanStarted?: (planId: number) => void;
	/** Changes when the run feeding this company progresses; the dossier refetches. */
	refreshToken?: string;
};

const DossierDrawer = ({
	candidate,
	left,
	onClose,
	onImport,
	onSync,
	onEnrichStarted,
	onPlanStarted,
	refreshToken
}: Props) => {
	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onClose();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [onClose]);

	return (
		<>
			<div
				className="absolute inset-y-0 right-0 z-20 bg-[rgba(30,45,60,.16)]"
				style={{ left }}
				onClick={onClose}
				aria-hidden
			/>
			<div
				role="dialog"
				aria-label={candidate.name || candidate.prospectingId}
				className="pw-slide-in absolute bottom-[14px] right-[18px] top-[14px] z-30 flex w-[760px] max-w-[calc(100%-36px)] flex-col overflow-hidden rounded-[8px] border border-grey-5 bg-background shadow-[0_20px_40px_rgba(0,0,0,.18)]"
			>
				<button
					type="button"
					onClick={onClose}
					aria-label={T('prospecting.v2.dossier.close')}
					className="absolute right-[18px] top-4 z-10 flex size-[26px] items-center justify-center rounded border border-grey-5 text-grey-10 transition-colors hover:bg-grey-1 hover:text-grey-13"
				>
					<X size={13} />
				</button>
				<div className="min-h-0 flex-1 overflow-y-auto">
					<LegacyEmbed variant="dossier">
						<CompanyDossier
							candidate={candidate}
							onBack={onClose}
							onImport={onImport}
							onSync={onSync}
							onEnrichStarted={onEnrichStarted}
							onPlanStarted={onPlanStarted ? planId => onPlanStarted(planId) : undefined}
							refreshToken={refreshToken}
						/>
					</LegacyEmbed>
				</div>
			</div>
		</>
	);
};

export default DossierDrawer;
